import React, { Component } from 'react'
import { render } from 'react-dom'
import { ApolloProvider, ApolloClient } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache'
import { createUploadLink } from 'apollo-upload-client'
import { concat } from '@apollo/client'
import { IntlProvider } from 'react-intl'
import { onError } from '@apollo/client/link/error'
import * as Sentry from '@sentry/browser'
import { Modal } from 'antd'
import 'antd/dist/antd.css'
import './styles/styles.css'
import 'moment/locale/fr'

import Routes from './router'
import * as Utils from './components/Utils'
import * as Auth from './auth'
import { versionCheckLink } from './versionCheckLink'
import { history } from './history'

const env = Utils.environment()

if (env === 'production' || env === 'staging') {
	Sentry.init({
		dsn: 'https://1b897be82649486c92d65d62b34d3fbe@sentry.io/1220128',
		environment: env,
		release: import.meta.env.VITE_APP_GIT_SHA,
		beforeBreadcrumb: (breadcrumb, hint) => {
			if (
				breadcrumb.category === 'http' &&
				breadcrumb.data.url.startsWith('http://127.0.0.1')
			) {
				return null
			}
			return breadcrumb
		}
	})
}

const link = createUploadLink({
	uri: `${window.location.origin}/graphql`,
	credentials: 'include',
	headers: { 'Apollo-Require-Preflight': 'true' }
})

let sessionExpiredModalShown = false

const showSessionExpiredModal = () => {
	// Only show the modal once to prevent multiple modals
	if (!sessionExpiredModalShown) {
		sessionExpiredModalShown = true

		// Save the current location to redirect back after login
		const currentPath = window.location.pathname + window.location.search

		// Get localized messages with fallbacks
		const language = Utils.language || 'en'
		const messages =
			Utils.messages?.[language] || Utils.messages?.['en'] || {}
		const title =
			messages['auth.session.expired.title'] || 'Session Expired'
		const content =
			messages['auth.session.expired.message'] ||
			'Your session has expired. Please log in again to continue.'
		const okText = messages['auth.session.expired.login'] || 'Log in'

		const handleConfirm = () => {
			// Clear user cache
			Auth.cache.user = null
			Auth.cache.team = null
			history.replace(`/login/${encodeURIComponent(currentPath)}`)
			sessionExpiredModalShown = false
		}

		// Show modal with session expired message
		Modal.warning({
			title,
			content,
			okText,
			onOk: handleConfirm,
			// go to login on cancel as well
			onCancel: handleConfirm
		})
	}
}

const errorLink = onError((req) => {
	// Check for network errors (including 401)
	if (req.networkError) {
		// Handle 401 Unauthorized errors only if user was previously logged in
		if (req.networkError.statusCode === 401 && Auth.cache.user) {
			showSessionExpiredModal()
			return
		}
	}

	// Handle GraphQL errors
	if (req.graphQLErrors) {
		for (let e of req.graphQLErrors) {
			// Handle NOT_LOGGED_IN error only if user was previously logged in
			if (e.message === 'NOT_LOGGED_IN' && Auth.cache.user) {
				showSessionExpiredModal()
				return
			}

			// Log other errors to Sentry if user is logged in
			if (Auth.cache.user) {
				let error = new Error(e.message)
				error.graphQLErrors = JSON.stringify(e)
				error.operation = JSON.stringify(req.operation)
				error.response = JSON.stringify(req.response)
				error.user = Auth.cache.user.username

				if (Utils.userFacingErrors.indexOf(e.message) === -1) {
					Sentry.captureException(error)
				}
			}
		}
	}
})

const client = new ApolloClient({
	link: concat(errorLink, concat(versionCheckLink, link)),
	cache: new InMemoryCache()
})

Utils.setLanguage()

class ApolloApp extends Component {
	state = {
		language: Utils.language
	}

	setLanguage = (language) => {
		if (language) {
			Utils.setLanguage(language)
			this.setState({ language })
		}
	}

	render() {
		return (
			<IntlProvider
				locale={this.state.language}
				messages={Utils.messages[this.state.language]}
			>
				<ApolloProvider client={client}>
					<Routes
						graphqlClient={client}
						setLanguage={this.setLanguage}
					/>
				</ApolloProvider>
			</IntlProvider>
		)
	}
}

render(<ApolloApp />, document.getElementById('root'))
